import translations from '@translationKeys'

import {
  CustomColumnType,
  DEFAULT_SHORTLIST_SORT,
  FilterOperator,
  ShortlistFilterField,
  ShortlistFilterFieldType,
  ShortlistSortOrder,
  ShortlistSortProfileValue,
} from './types'

export const SHORTLIST_STATUS_MAP = {
  ENABLED: 1,
  DISABLED: 0,
}

export const DEFAULT_SHORTLIST_PROFILES_PER_PAGE = 50

/**
 * Used for displaying the list of options
 * in the shortlist sort popover
 */
export const MAP_FIELDS_TO_SORT_OPTION_VALUES: Record<string, ShortlistSortProfileValue[]> = {
  profileTitle: [
    ShortlistSortProfileValue.ASSET_COMPANY_TITLE,
    ShortlistSortProfileValue.DATE_ADDED_ASCENDING,
    ShortlistSortProfileValue.DATE_ADDED_DESCENDING,
  ],
  profileEventName: [
    ShortlistSortProfileValue.NEWEST_EVENT,
    ShortlistSortProfileValue.OLDEST_EVENT,
    ShortlistSortProfileValue.DATE_ADDED_ASCENDING,
    ShortlistSortProfileValue.DATE_ADDED_DESCENDING,
  ],
  contactStatus: [
    ShortlistSortProfileValue.STATUS_TYPE,
    ShortlistSortProfileValue.DATE_ADDED_ASCENDING,
    ShortlistSortProfileValue.DATE_ADDED_DESCENDING,
  ],
  profileUpdatedDate: [
    ShortlistSortProfileValue.DATE_UPDATED_ASCENDING,
    ShortlistSortProfileValue.DATE_UPDATED_DESCENDING,
    ShortlistSortProfileValue.DATE_ADDED_ASCENDING,
    ShortlistSortProfileValue.DATE_ADDED_DESCENDING,
  ],
  tagId: [
    ShortlistSortProfileValue.STATUS,
    ShortlistSortProfileValue.DATE_ADDED_ASCENDING,
    ShortlistSortProfileValue.DATE_ADDED_DESCENDING,
  ],
  nda: [
    ShortlistSortProfileValue.DONE,
    ShortlistSortProfileValue.NOT_DONE,
    ShortlistSortProfileValue.DATE_ADDED_ASCENDING,
    ShortlistSortProfileValue.DATE_ADDED_DESCENDING,
  ],
  note: [
    ShortlistSortProfileValue.NEWEST_NOTE,
    ShortlistSortProfileValue.OLDEST_NOTE,
    ShortlistSortProfileValue.DATE_ADDED_ASCENDING,
    ShortlistSortProfileValue.DATE_ADDED_DESCENDING,
  ],
  futureEvents: [
    ShortlistSortProfileValue.NEXT_EVENT,
    ShortlistSortProfileValue.DATE_ADDED_ASCENDING,
    ShortlistSortProfileValue.DATE_ADDED_DESCENDING,
  ],
  lastEvent: [
    ShortlistSortProfileValue.MOST_RECENT_EVENT_FIRST,
    ShortlistSortProfileValue.DATE_ADDED_ASCENDING,
    ShortlistSortProfileValue.DATE_ADDED_DESCENDING,
  ],
  priority: [
    ShortlistSortProfileValue.PRIORITY_HIGH_TO_NOT_SET,
    ShortlistSortProfileValue.PRIORITY_NOT_SET_TO_HIGH,
    ShortlistSortProfileValue.DATE_ADDED_ASCENDING,
    ShortlistSortProfileValue.DATE_ADDED_DESCENDING,
  ],
  company: [ShortlistSortProfileValue.COMPANY_NAME_ASC, ShortlistSortProfileValue.COMPANY_NAME_DESC],
  customFieldTextValues: [
    ShortlistSortProfileValue.NEWEST_TEXTS,
    ShortlistSortProfileValue.OLDEST_TEXTS,
    ShortlistSortProfileValue.DATE_ADDED_ASCENDING,
    ShortlistSortProfileValue.DATE_ADDED_DESCENDING,
  ],
  updatedAt: [ShortlistSortProfileValue.DATE_ADDED_ASCENDING, ShortlistSortProfileValue.DATE_ADDED_DESCENDING],
  frequency: [ShortlistSortProfileValue.DATE_ADDED_ASCENDING, ShortlistSortProfileValue.DATE_ADDED_DESCENDING],
}

export const SHORTLIST_FILTERS: { attribute: ShortlistFilterField; label: string }[] = [
  {
    attribute: ShortlistFilterField.title,
    label: translations.CUSTOM_LIST_TABLE_HEADER_TITLE,
  },
  {
    attribute: ShortlistFilterField.companyName,
    label: translations.CUSTOM_LIST_TABLE_HEADER_COMPANY_NAME,
  },
  {
    attribute: ShortlistFilterField.lastEvent,
    label: translations.CUSTOM_LIST_TABLE_HEADER_LAST_EVENT,
  },
  {
    attribute: ShortlistFilterField.nda,
    label: translations.CUSTOM_LIST_TABLE_HEADER_NDA,
  },
  {
    attribute: ShortlistFilterField.priority,
    label: translations.CUSTOM_LIST_TABLE_HEADER_PRIORITY,
  },
  {
    attribute: ShortlistFilterField.tagId,
    label: translations.CUSTOM_LIST_TABLE_HEADER_CUSTOM_TAG,
  },
  {
    attribute: ShortlistFilterField.createdAt,
    label: translations.CUSTOM_LIST_TABLE_HEADER_DATE_ADDED,
  },
  {
    attribute: ShortlistFilterField.updatedAt,
    label: translations.CUSTOM_LIST_TABLE_HEADER_UPDATED,
  },
  {
    attribute: ShortlistFilterField.customFieldValues,
    label: '',
  },
  {
    attribute: ShortlistFilterField.note,
    label: translations.CUSTOM_LIST_TABLE_HEADER_NOTES,
  },
  {
    attribute: ShortlistFilterField.phase,
    label: translations.CUSTOM_LIST_TABLE_HEADER_PHASE,
  },
]

export const SHORTLIST_FILTER_OPERATORS_BY_FIELD: Record<ShortlistFilterField, FilterOperator[]> = {
  title: [FilterOperator.CONTAINS, FilterOperator.EQUALS],
  companyName: [FilterOperator.CONTAINS, FilterOperator.EQUALS, FilterOperator.IS_EMPTY, FilterOperator.IS_NOT_EMPTY],
  lastEvent: [FilterOperator.CONTAINS, FilterOperator.EQUALS, FilterOperator.IS_EMPTY, FilterOperator.IS_NOT_EMPTY],
  nda: [FilterOperator.IS_EMPTY, FilterOperator.IS_NOT_EMPTY],
  priority: [FilterOperator.EQUALS, FilterOperator.IS_EMPTY, FilterOperator.IS_NOT_EMPTY],
  tagId: [FilterOperator.CONTAINS, FilterOperator.EQUALS, FilterOperator.IS_EMPTY, FilterOperator.IS_NOT_EMPTY],
  note: [FilterOperator.CONTAINS, FilterOperator.IS_EMPTY, FilterOperator.IS_NOT_EMPTY],
  phase: [FilterOperator.CONTAINS, FilterOperator.EQUALS, FilterOperator.IS_EMPTY, FilterOperator.IS_NOT_EMPTY],
  updatedAt: [FilterOperator.BETWEEN],
  createdAt: [FilterOperator.BETWEEN],
  customFieldValues: [],
}

export const SHORTLIST_FILTER_OPERATORS_BY_CUSTOM_FIELD: Record<CustomColumnType, FilterOperator[]> = {
  text: [FilterOperator.CONTAINS, FilterOperator.IS_EMPTY, FilterOperator.IS_NOT_EMPTY],
  autocomplete: [FilterOperator.EQUALS, FilterOperator.IS_EMPTY, FilterOperator.IS_NOT_EMPTY],
}

export const SHORTLIST_SEARCH_DEBOUNCE_TIME = 300

export const getShortlistNextSortOrder = (
  isSortActive: boolean,
  currentSortOrder?: ShortlistSortOrder | null,
  sortFieldName?: string,
) => {
  switch (true) {
    case isSortActive && currentSortOrder === ShortlistSortOrder.ASC:
      return ShortlistSortOrder.DESC
    case isSortActive && currentSortOrder === ShortlistSortOrder.DESC && sortFieldName === DEFAULT_SHORTLIST_SORT.fieldName:
      return ShortlistSortOrder.ASC
    case isSortActive && currentSortOrder === ShortlistSortOrder.DESC:
      return undefined
    default:
      return ShortlistSortOrder.ASC
  }
}

export const SHORTLIST_VALUE_TYPE_BY_OPERATOR: Record<FilterOperator, ShortlistFilterFieldType | undefined> = {
  [FilterOperator.CONTAINS]: ShortlistFilterFieldType.TEXT,
  [FilterOperator.IS_EMPTY]: undefined,
  [FilterOperator.IS_NOT_EMPTY]: undefined,
  [FilterOperator.EQUALS]: ShortlistFilterFieldType.TEXT,
  [FilterOperator.BETWEEN]: ShortlistFilterFieldType.DATE_RANGE,
}
